import { useLocation, useNavigate } from "@tanstack/react-router";
import { localizePathFor, stripLocaleFromPath, useT, type Locale } from "@/lib/i18n/I18nProvider";
import { cn } from "@/lib/utils";

/** Serialize TanStack's parsed search object into a "?a=b&c=d" string for hrefs. */
function serializeSearch(search: unknown): string {
  if (!search) return "";
  if (typeof search === "string") {
    return search === "" || search.startsWith("?") ? search : `?${search}`;
  }
  if (typeof search !== "object") return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(search as Record<string, unknown>)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) params.append(k, String(item));
    } else {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function normalizeHash(hash: unknown): string {
  if (!hash || typeof hash !== "string") return "";
  return hash.startsWith("#") ? hash : `#${hash}`;
}

/** Build the full href (path + ?search + #hash) for the locale-swapped URL. */
export function buildLocaleHref(
  pathname: string,
  search: unknown,
  hash: unknown,
  target: Locale,
) {
  const stripped = stripLocaleFromPath(pathname);
  const path = localizePathFor(target, stripped);
  return path + serializeSearch(search) + normalizeHash(hash);
}

/** Just the path portion, for handing to TanStack's typed navigate(). */
export function buildLocalePath(pathname: string, target: Locale) {
  return localizePathFor(target, stripLocaleFromPath(pathname));
}

export function persistLocaleCookie(target: Locale) {
  if (typeof document === "undefined") return;
  document.cookie = `perceptica_locale=${target}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}

/** True when a click event is a modifier/non-primary click that should fall through to native nav. */
function isModifiedClick(e: React.MouseEvent) {
  return (
    e.defaultPrevented ||
    e.button !== 0 ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey
  );
}

/**
 * Shared click handler used by the header pill and the footer language
 * controls. Performs an in-SPA navigation that preserves the current search
 * and hash, pushing a new history entry so browser back/forward swap locales
 * predictably. Modifier-clicks fall through because the button performs no
 * action on its own.
 */
export function useLocaleSwitchHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale: currentLocale } = useT();

  return (target: Locale) => (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    e.preventDefault();
    if (target === currentLocale) return;
    persistLocaleCookie(target);
    const nextPath = buildLocalePath(location.pathname, target);
    // Cast: `to` is a string at runtime, the typed-router union doesn't cover dynamic locale paths.
    navigate({
      to: nextPath as unknown as "/",
      search: location.search as Record<string, unknown>,
      hash: typeof location.hash === "string" && location.hash ? location.hash : undefined,
      replace: false,
    });
  };
}

export function LocaleSwitcher({ dark = false }: { dark?: boolean }) {
  const { locale } = useT();
  const handle = useLocaleSwitchHandler();

  const base =
    "inline-flex h-8 items-center gap-1 rounded-full border px-2 text-[11px] font-semibold tracking-[0.08em]";
  const cls = dark
    ? "border-white/20 text-white/80"
    : "border-border/70 text-foreground/75";
  const btnActive = dark ? "bg-white/15 text-white" : "bg-secondary text-foreground";
  const btnIdle = dark ? "hover:text-white" : "hover:text-foreground";

  return (
    <div className={cn(base, cls)} role="group" aria-label="Language">
      <button
        type="button"
        onClick={handle("en")}
        aria-pressed={locale === "en"}
        className={cn("rounded-full px-2 py-0.5 transition-colors", locale === "en" ? btnActive : btnIdle)}
      >
        EN
      </button>
      <button
        type="button"
        onClick={handle("bg")}
        aria-pressed={locale === "bg"}
        className={cn("rounded-full px-2 py-0.5 transition-colors", locale === "bg" ? btnActive : btnIdle)}
      >
        БГ
      </button>
    </div>
  );
}
